import React, {useCallback, useEffect, useState} from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle, Divider, SvgIcon} from "@mui/material";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import CopyToClipboard from "../../base/copy-to-clipboard";
import IconButton from "../../base/button/icon_button";
import {ReactComponent as Copy} from "../../../../assets/images/copy.svg";
import {openLinkInNewTab} from "../../../../core/services/utils/utils";

const initialAppInfo = {
    downloadLink: '',
    activationKey: '',
}

const DownloadPrinterHubAppDialog = ({
                                         open,
                                         onClose,
                                     }) => {
    const [loading, setLoading] = useState(false);
    const [appInfo, setAppInfo] = useState(initialAppInfo);
    const isMounted = useIsMounted();

    /**
     * Fetches the app info from the api.
     * @type {(function(): Promise<void>)}
     */
    const getAppInfo = useCallback(async () => {
        setLoading(true);
        const response = await Api.gtPrinterHubApplicationInfo();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setAppInfo({
                downloadLink: response.data.applicationDownloadLink,
                activationKey: response.data.key,
            });
        }
    }, [isMounted]);

    /**
     * As soon as the dialog is opened:
     * - fetches the app info from the server.
     */
    useEffect(() => {
        if (!open)
            return;
        getAppInfo().then();
    }, [getAppInfo, open])

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={'sm'}
                fullWidth
            >
                <DialogTitle>
                    Download Printer Hub Application
                </DialogTitle>
                <DialogContent>
                    {
                        loading
                            ? <LoadingLottieAnimation width={'90%'}/>
                            : <Col xs={12} className=' mb-4'>
                                <p className='text-sm text-color-1 mb-3'>
                                    use the following activation key to activate the application after downloading it.
                                </p>
                                <p className='text-sm text-color-1 font-weight-semi-bold mb-2'>
                                    Activation key:
                                </p>
                                <Row
                                    className={`background-secondary-color-3 rounded-xl no-gutters align-items-center justify-content-between px-3 py-2`}>
                                    <p className='text-color-1 text-sm font-weight-semi-bold w-75'>
                                        {appInfo.activationKey ?? ''}
                                    </p>
                                    <div className={'d-flex align-items-center justify-content-end'}>
                                        {
                                            appInfo.activationKey &&
                                            <CopyToClipboard>
                                                {({copy}) =>
                                                    <IconButton
                                                        onPressed={() => copy(appInfo.activationKey ?? '')}
                                                        color={'secondary'}
                                                        className={'copy-to-clipboard'}
                                                    >
                                                        <SvgIcon className='icon normal' component={Copy}/>
                                                    </IconButton>
                                                }
                                            </CopyToClipboard>
                                        }
                                    </div>
                                </Row>
                            </Col>
                    }
                </DialogContent>
                <DialogActions className={'p-0 m-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-end align-items-start w-100'>
                            <Button
                                disabled={!open || loading}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => openLinkInNewTab(appInfo.downloadLink)}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {loading ? "Loading..." : "Download App"}
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}
export default DownloadPrinterHubAppDialog;
